import React, { useState, useEffect, useCallback } from 'react';
import SelectBuscable from '../components/SelectBuscable';
import { supabase } from '../supabaseClient';
import { useCompany } from '../CompanyContext';
import { useImpersonation } from '../ImpersonationContext';
import { hoyEc } from '../lib/fechas';

// ── Types ──────────────────────────────────────────────────────────────────────

type PlanStatus = 'borrador' | 'vigente';

interface EmergencyPlan {
  id:                    string;
  company_id:            string;
  threats:               string | null;
  procedures:            string | null;
  resource_map_notes:    string | null;
  evacuation_map_notes:  string | null;
  maintenance_schedule:  string | null;
  status:                PlanStatus;
  created_by:            string | null;
  created_at:            string;
  updated_at:            string;
}

type BrigadeRole = 'evacuacion' | 'primeros_auxilios' | 'contra_incendios' | 'jefe_brigada';

interface BrigadeMember {
  id:            string;
  company_id:    string;
  worker_id:     string;
  role:          BrigadeRole;
  trained:       boolean;
  training_date: string | null;
  created_at:    string;
}

type DrillType = 'incendio' | 'evacuacion' | 'sismo' | 'emergencia_medica' | 'otro';

interface EmergencyDrill {
  id:                  string;
  company_id:          string;
  calendar_event_id:   string | null;
  drill_date:          string;
  drill_type:          DrillType;
  location:            string | null;
  duration_minutes:    number | null;
  participants_count:  number | null;
  incidents_notes:     string | null;
  lessons_learned:     string | null;
  photo_urls:          string[] | null;
  created_by:          string | null;
  created_at:          string;
}

interface WorkerOption { id: string; full_name: string; cedula: string | null; }

type Section = 'plan' | 'brigada' | 'simulacros';

// ── Constants ──────────────────────────────────────────────────────────────────

const PLAN_STATUS_LABELS: Record<PlanStatus, string> = {
  borrador: 'Borrador',
  vigente:  'Vigente',
};

const ROLE_LABELS: Record<BrigadeRole, string> = {
  evacuacion:        'Evacuación',
  primeros_auxilios: 'Primeros Auxilios',
  contra_incendios:  'Contra Incendios',
  jefe_brigada:      'Jefe de Brigada',
};

const DRILL_TYPE_LABELS: Record<DrillType, string> = {
  incendio:           'Incendio',
  evacuacion:         'Evacuación',
  sismo:              'Sismo',
  emergencia_medica:  'Emergencia Médica',
  otro:               'Otro',
};

function makeEmptyPlanForm() {
  return {
    threats:              '',
    procedures:           '',
    resource_map_notes:   '',
    evacuation_map_notes: '',
    maintenance_schedule: '',
    status:               'borrador' as PlanStatus,
  };
}

function makeEmptyMemberForm() {
  return {
    worker_id:     '',
    role:          'evacuacion' as BrigadeRole,
    trained:       false,
    training_date: '',
  };
}

function makeEmptyDrillForm() {
  return {
    drill_date:         hoyEc(),
    drill_type:         'evacuacion' as DrillType,
    location:           '',
    duration_minutes:   '',
    participants_count: '',
    incidents_notes:    '',
    lessons_learned:    '',
  };
}

// ── Component ──────────────────────────────────────────────────────────────────

export default function EmergencyPlanPage() {
  const { company, profile } = useCompany();
  const { impersonated }     = useImpersonation();
  const effectiveCompanyId   = (impersonated ?? company)?.id ?? '';

  const canEdit   = profile?.role === 'admin' || profile?.role === 'manager' || !!impersonated;
  const canDelete = profile?.role === 'admin' || !!impersonated;

  const [section, setSection] = useState<Section>('plan');
  const [workers, setWorkers] = useState<WorkerOption[]>([]);
  const [loading, setLoading] = useState(true);

  // ── Plan de Emergencias ──────────────────────────────────────────────────────
  const [plan,          setPlan]          = useState<EmergencyPlan | null>(null);
  const [planEditing,   setPlanEditing]   = useState(false);
  const [planForm,      setPlanForm]      = useState(makeEmptyPlanForm);
  const [planSaving,    setPlanSaving]    = useState(false);
  const [planFormError, setPlanFormError] = useState('');

  // ── Brigada de Emergencia ────────────────────────────────────────────────────
  const [members,          setMembers]          = useState<BrigadeMember[]>([]);
  const [showAddMember,    setShowAddMember]    = useState(false);
  const [memberForm,       setMemberForm]       = useState(makeEmptyMemberForm);
  const [memberSaving,     setMemberSaving]     = useState(false);
  const [memberFormError,  setMemberFormError]  = useState('');
  const [deletingMemberId, setDeletingMemberId] = useState<string | null>(null);

  // ── Simulacros Ejecutados ────────────────────────────────────────────────────
  const [drills,          setDrills]          = useState<EmergencyDrill[]>([]);
  const [showDrillModal,  setShowDrillModal]  = useState(false);
  const [drillForm,       setDrillForm]       = useState(makeEmptyDrillForm);
  const [drillSaving,     setDrillSaving]     = useState(false);
  const [drillFormError,  setDrillFormError]  = useState('');

  // ── Load ────────────────────────────────────────────────────────────────────

  const loadData = useCallback(async () => {
    if (!effectiveCompanyId) return;
    setLoading(true);
    const [{ data: pl }, { data: mem }, { data: dr }, { data: wk }] = await Promise.all([
      supabase.from('emergency_plans').select('*').eq('company_id', effectiveCompanyId).maybeSingle(),
      supabase.from('emergency_brigade_members').select('*').eq('company_id', effectiveCompanyId).order('created_at'),
      supabase.from('emergency_drills').select('*').eq('company_id', effectiveCompanyId).order('drill_date', { ascending: false }),
      supabase.from('workers').select('id, full_name, cedula').eq('company_id', effectiveCompanyId).eq('status', 'activo').order('full_name'),
    ]);
    setPlan((pl ?? null) as EmergencyPlan | null);
    setMembers((mem ?? []) as BrigadeMember[]);
    setDrills((dr ?? []) as EmergencyDrill[]);
    setWorkers((wk ?? []) as WorkerOption[]);
    setLoading(false);
  }, [effectiveCompanyId]);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Plan handlers ────────────────────────────────────────────────────────────

  function openPlanForm() {
    if (plan) {
      setPlanForm({
        threats:              plan.threats ?? '',
        procedures:           plan.procedures ?? '',
        resource_map_notes:   plan.resource_map_notes ?? '',
        evacuation_map_notes: plan.evacuation_map_notes ?? '',
        maintenance_schedule: plan.maintenance_schedule ?? '',
        status:               plan.status,
      });
    } else {
      setPlanForm(makeEmptyPlanForm());
    }
    setPlanFormError('');
    setPlanEditing(true);
  }

  async function handleSavePlan() {
    setPlanSaving(true);
    setPlanFormError('');

    const payload = {
      company_id:           effectiveCompanyId,
      threats:               planForm.threats.trim() || null,
      procedures:            planForm.procedures.trim() || null,
      resource_map_notes:    planForm.resource_map_notes.trim() || null,
      evacuation_map_notes:  planForm.evacuation_map_notes.trim() || null,
      maintenance_schedule:  planForm.maintenance_schedule.trim() || null,
      status:                planForm.status,
      updated_at:            new Date().toISOString(),
      ...(plan ? {} : { created_by: profile?.id ?? null }),
    };

    const { error } = await supabase
      .from('emergency_plans')
      .upsert(payload, { onConflict: 'company_id' });

    if (error) {
      setPlanFormError('Error al guardar el plan: ' + error.message);
      setPlanSaving(false);
      return;
    }

    setPlanSaving(false);
    setPlanEditing(false);
    await loadData();
  }

  // ── Brigada handlers ─────────────────────────────────────────────────────────

  function openAddMember() {
    setMemberForm(makeEmptyMemberForm());
    setMemberFormError('');
    setShowAddMember(true);
  }

  async function handleSaveMember() {
    if (!memberForm.worker_id) { setMemberFormError('Selecciona un trabajador.'); return; }
    if (memberForm.trained && !memberForm.training_date) {
      setMemberFormError('Indica la fecha de capacitación o desmarca "Capacitado".');
      return;
    }
    setMemberSaving(true);
    setMemberFormError('');

    const { error } = await supabase.from('emergency_brigade_members').insert({
      company_id:    effectiveCompanyId,
      worker_id:     memberForm.worker_id,
      role:          memberForm.role,
      trained:       memberForm.trained,
      training_date: memberForm.trained ? memberForm.training_date : null,
    });

    if (error) {
      setMemberFormError('Error al agregar miembro: ' + error.message);
      setMemberSaving(false);
      return;
    }

    setMemberSaving(false);
    setShowAddMember(false);
    await loadData();
  }

  async function handleDeleteMember(m: BrigadeMember) {
    const worker = workers.find(w => w.id === m.worker_id);
    if (!window.confirm(`¿Quitar a ${worker?.full_name ?? 'este trabajador'} de la brigada de emergencia?`)) return;
    setDeletingMemberId(m.id);
    const { error } = await supabase.from('emergency_brigade_members').delete().eq('id', m.id);
    setDeletingMemberId(null);
    if (error) { alert('Error al eliminar: ' + error.message); return; }
    await loadData();
  }

  // ── Simulacros handlers ──────────────────────────────────────────────────────

  function openDrillModal() {
    setDrillForm(makeEmptyDrillForm());
    setDrillFormError('');
    setShowDrillModal(true);
  }

  async function handleSaveDrill() {
    if (!drillForm.drill_date) { setDrillFormError('La fecha del simulacro es requerida.'); return; }
    setDrillSaving(true);
    setDrillFormError('');

    const { error } = await supabase.from('emergency_drills').insert({
      company_id:          effectiveCompanyId,
      calendar_event_id:   null,
      drill_date:          drillForm.drill_date,
      drill_type:          drillForm.drill_type,
      location:            drillForm.location.trim() || null,
      duration_minutes:    drillForm.duration_minutes ? parseInt(drillForm.duration_minutes, 10) : null,
      participants_count:  drillForm.participants_count ? parseInt(drillForm.participants_count, 10) : null,
      incidents_notes:     drillForm.incidents_notes.trim() || null,
      lessons_learned:     drillForm.lessons_learned.trim() || null,
      created_by:          profile?.id ?? null,
    });

    if (error) {
      setDrillFormError('Error al registrar el simulacro: ' + error.message);
      setDrillSaving(false);
      return;
    }

    setDrillSaving(false);
    setShowDrillModal(false);
    await loadData();
  }

  // ── Pendientes normativos ────────────────────────────────────────────────────

  const noPlanCreated   = !loading && !plan;
  const brigadeEmpty    = !loading && members.length === 0;

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* Header */}
      <div>
        <h2 style={{ color: '#f1f5f9', fontSize: '1.25rem', fontWeight: 700, margin: 0 }}>Plan de Emergencias</h2>
        <p style={{ color: '#64748b', fontSize: '0.82rem', margin: '4px 0 0' }}>
          Plan de contingencia, brigada de emergencia y registro de simulacros — Decisión 584 Art. 16, DE 255 Art. 58
        </p>
      </div>

      {/* Pendientes normativos */}
      {(noPlanCreated || brigadeEmpty) && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {noPlanCreated && (
            <div style={warningBannerStyle}>⚠ Aún no se ha creado el Plan de Emergencias de la empresa.</div>
          )}
          {brigadeEmpty && (
            <div style={warningBannerStyle}>⚠ La Brigada de Emergencia no tiene miembros registrados.</div>
          )}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8, borderBottom: '1px solid #334155' }}>
        {([
          { id: 'plan',       label: 'Plan de Emergencias' },
          { id: 'brigada',    label: 'Brigada de Emergencia' },
          { id: 'simulacros', label: 'Simulacros Ejecutados' },
        ] as { id: Section; label: string }[]).map(t => (
          <button
            key={t.id}
            onClick={() => setSection(t.id)}
            style={{
              ...tabBtnStyle,
              ...(section === t.id ? tabBtnActiveStyle : {}),
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#475569', fontSize: '0.88rem' }}>Cargando…</div>
      ) : (
        <>
          {/* ── SECCIÓN 1: Plan de Emergencias ─────────────────────────────── */}
          {section === 'plan' && (
            <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 14, padding: 20 }}>
              {!planEditing ? (
                plan ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={statusBadgeStyle(plan.status)}>{PLAN_STATUS_LABELS[plan.status]}</span>
                      {canEdit && <button onClick={openPlanForm} style={ghostBtn}>Editar plan</button>}
                    </div>
                    <PlanField label="Amenazas identificadas" value={plan.threats} />
                    <PlanField label="Procedimientos" value={plan.procedures} />
                    <PlanField label="Mapa de recursos" value={plan.resource_map_notes} />
                    <PlanField label="Mapa de evacuación" value={plan.evacuation_map_notes} />
                    <PlanField label="Cronograma de mantenimiento" value={plan.maintenance_schedule} />
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: 24 }}>
                    <p style={{ color: '#64748b', fontSize: '0.88rem', margin: '0 0 16px' }}>
                      Esta empresa aún no tiene un Plan de Emergencias registrado.
                    </p>
                    {canEdit && (
                      <button onClick={openPlanForm} style={primaryBtn}>+ Crear Plan de Emergencias</button>
                    )}
                  </div>
                )
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <Field label="Amenazas identificadas">
                    <textarea style={{ ...ta, minHeight: 70 }} value={planForm.threats}
                      onChange={e => setPlanForm(f => ({ ...f, threats: e.target.value }))}
                      placeholder="Amenazas naturales, tecnológicas, antrópicas…" />
                  </Field>
                  <Field label="Procedimientos">
                    <textarea style={{ ...ta, minHeight: 70 }} value={planForm.procedures}
                      onChange={e => setPlanForm(f => ({ ...f, procedures: e.target.value }))}
                      placeholder="Procedimientos de respuesta ante cada tipo de emergencia…" />
                  </Field>
                  <Field label="Mapa de recursos">
                    <textarea style={{ ...ta, minHeight: 60 }} value={planForm.resource_map_notes}
                      onChange={e => setPlanForm(f => ({ ...f, resource_map_notes: e.target.value }))}
                      placeholder="Ubicación de extintores, botiquines, alarmas…" />
                  </Field>
                  <Field label="Mapa de evacuación">
                    <textarea style={{ ...ta, minHeight: 60 }} value={planForm.evacuation_map_notes}
                      onChange={e => setPlanForm(f => ({ ...f, evacuation_map_notes: e.target.value }))}
                      placeholder="Rutas de evacuación, puntos de encuentro…" />
                  </Field>
                  <Field label="Cronograma de mantenimiento">
                    <textarea style={{ ...ta, minHeight: 60 }} value={planForm.maintenance_schedule}
                      onChange={e => setPlanForm(f => ({ ...f, maintenance_schedule: e.target.value }))}
                      placeholder="Revisión de extintores, señalización, luces de emergencia…" />
                  </Field>
                  <Field label="Estado">
                    <select style={sel} value={planForm.status}
                      onChange={e => setPlanForm(f => ({ ...f, status: e.target.value as PlanStatus }))}>
                      {(Object.keys(PLAN_STATUS_LABELS) as PlanStatus[]).map(s =>
                        <option key={s} value={s}>{PLAN_STATUS_LABELS[s]}</option>
                      )}
                    </select>
                  </Field>
                  {planFormError && <p style={{ color: '#ef4444', fontSize: '0.82rem', margin: 0 }}>{planFormError}</p>}
                  <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                    <button onClick={() => setPlanEditing(false)} style={cancelBtn} disabled={planSaving}>Cancelar</button>
                    <button onClick={handleSavePlan} disabled={planSaving} style={{ ...saveBtn, opacity: planSaving ? 0.7 : 1 }}>
                      {planSaving ? 'Guardando…' : 'Guardar plan'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── SECCIÓN 2: Brigada de Emergencia ───────────────────────────── */}
          {section === 'brigada' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                {canEdit && (
                  <button onClick={openAddMember} style={primaryBtn}>+ Agregar miembro</button>
                )}
              </div>
              <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 14, overflow: 'hidden' }}>
                {members.length === 0 ? (
                  <div style={{ padding: 48, textAlign: 'center', color: '#475569', fontSize: '0.88rem' }}>
                    Aún no hay miembros registrados en la brigada de emergencia.
                  </div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.84rem' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid #334155' }}>
                          {['Trabajador', 'Rol', 'Capacitado', 'Fecha capacitación', ''].map(h => (
                            <th key={h} style={thStyle}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {members.map(m => {
                          const w = workers.find(x => x.id === m.worker_id);
                          return (
                            <tr key={m.id} style={{ borderBottom: '1px solid rgba(51,65,85,0.5)' }}>
                              <td style={td}>
                                <div style={{ color: '#e2e8f0' }}>{w?.full_name ?? '—'}</div>
                                {w?.cedula && <div style={{ color: '#64748b', fontSize: '0.74rem' }}>{w.cedula}</div>}
                              </td>
                              <td style={td}>{ROLE_LABELS[m.role]}</td>
                              <td style={td}>
                                <span style={{ color: m.trained ? '#2FBF71' : '#64748b', fontWeight: 700 }}>
                                  {m.trained ? '✓ Sí' : 'No'}
                                </span>
                              </td>
                              <td style={{ ...td, color: '#94a3b8' }}>
                                {m.training_date ? new Date(m.training_date + 'T00:00:00').toLocaleDateString('es-EC') : '—'}
                              </td>
                              <td style={{ ...td, textAlign: 'right' }}>
                                {canDelete && (
                                  <button
                                    onClick={() => handleDeleteMember(m)}
                                    disabled={deletingMemberId === m.id}
                                    style={ghostBtn}
                                    onMouseEnter={e => { e.currentTarget.style.borderColor = '#ef4444'; e.currentTarget.style.color = '#ef4444'; }}
                                    onMouseLeave={e => { e.currentTarget.style.borderColor = '#334155'; e.currentTarget.style.color = '#94a3b8'; }}
                                  >
                                    {deletingMemberId === m.id ? 'Eliminando…' : 'Eliminar'}
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── SECCIÓN 3: Simulacros Ejecutados ───────────────────────────── */}
          {section === 'simulacros' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                {canEdit && (
                  <button onClick={openDrillModal} style={primaryBtn}>+ Registrar simulacro</button>
                )}
              </div>
              <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 14, overflow: 'hidden' }}>
                {drills.length === 0 ? (
                  <div style={{ padding: 48, textAlign: 'center', color: '#475569', fontSize: '0.88rem' }}>
                    Aún no hay simulacros registrados.
                  </div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.84rem' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid #334155' }}>
                          {['Fecha', 'Tipo', 'Lugar', 'Duración', 'Participantes', 'Notas'].map(h => (
                            <th key={h} style={thStyle}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {drills.map(d => (
                          <tr key={d.id} style={{ borderBottom: '1px solid rgba(51,65,85,0.5)' }}>
                            <td style={{ ...td, whiteSpace: 'nowrap' }}>
                              {new Date(d.drill_date + 'T00:00:00').toLocaleDateString('es-EC')}
                            </td>
                            <td style={td}>{DRILL_TYPE_LABELS[d.drill_type]}</td>
                            <td style={{ ...td, color: '#94a3b8' }}>{d.location ?? '—'}</td>
                            <td style={{ ...td, color: '#94a3b8' }}>
                              {d.duration_minutes != null ? `${d.duration_minutes} min` : '—'}
                            </td>
                            <td style={{ ...td, color: '#94a3b8', textAlign: 'center' }}>
                              {d.participants_count ?? '—'}
                            </td>
                            <td style={{ ...td, color: '#94a3b8', maxWidth: 260 }}>
                              {d.incidents_notes ?? '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* ── Modal: Agregar miembro de brigada ────────────────────────────────── */}
      {showAddMember && (
        <div style={overlay} onClick={e => e.target === e.currentTarget && !memberSaving && setShowAddMember(false)}>
          <div style={{ ...modalBox, maxWidth: 480 }}>
            <div style={modalHeader}>
              <h2 style={{ color: '#f1f5f9', fontSize: '1.05rem', fontWeight: 700, margin: 0 }}>Agregar miembro a la brigada</h2>
              <button onClick={() => !memberSaving && setShowAddMember(false)} style={closeBtn}>✕</button>
            </div>
            <div style={{ padding: '0 24px 24px', display: 'flex', flexDirection: 'column', gap: 14, marginTop: 20 }}>
              <Field label="Trabajador *">
                <SelectBuscable inputStyle={{ ...inp, paddingRight: 32 }} ariaLabel="Trabajador" value={memberForm.worker_id}
                  onChange={v => setMemberForm(f => ({ ...f, worker_id: v }))} vacio="— Seleccionar —"
                  opciones={workers.map(w => ({ value: w.id, label: `${w.full_name}${w.cedula ? ` (${w.cedula})` : ''}` }))} />
              </Field>
              <Field label="Rol en la brigada *">
                <select style={sel} value={memberForm.role}
                  onChange={e => setMemberForm(f => ({ ...f, role: e.target.value as BrigadeRole }))}>
                  {(Object.keys(ROLE_LABELS) as BrigadeRole[]).map(r =>
                    <option key={r} value={r}>{ROLE_LABELS[r]}</option>
                  )}
                </select>
              </Field>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={memberForm.trained}
                  onChange={e => setMemberForm(f => ({ ...f, trained: e.target.checked }))}
                  style={{ width: 16, height: 16, accentColor: '#2FBF71', cursor: 'pointer' }}
                />
                <span style={{ color: '#cbd5e1', fontSize: '0.86rem' }}>Capacitado</span>
              </label>
              {memberForm.trained && (
                <Field label="Fecha de capacitación">
                  <input style={inp} type="date" value={memberForm.training_date}
                    onChange={e => setMemberForm(f => ({ ...f, training_date: e.target.value }))} />
                </Field>
              )}
              {memberFormError && <p style={{ color: '#ef4444', fontSize: '0.82rem', margin: 0 }}>{memberFormError}</p>}
            </div>
            <div style={modalFooter}>
              <button onClick={() => !memberSaving && setShowAddMember(false)} style={cancelBtn}>Cancelar</button>
              <button onClick={handleSaveMember} disabled={memberSaving} style={{ ...saveBtn, opacity: memberSaving ? 0.7 : 1 }}>
                {memberSaving ? 'Guardando…' : 'Agregar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: Registrar simulacro ────────────────────────────────────────── */}
      {showDrillModal && (
        <div style={overlay} onClick={e => e.target === e.currentTarget && !drillSaving && setShowDrillModal(false)}>
          <div style={modalBox}>
            <div style={modalHeader}>
              <h2 style={{ color: '#f1f5f9', fontSize: '1.05rem', fontWeight: 700, margin: 0 }}>Registrar simulacro</h2>
              <button onClick={() => !drillSaving && setShowDrillModal(false)} style={closeBtn}>✕</button>
            </div>
            <div style={{ padding: '0 24px 24px', display: 'flex', flexDirection: 'column', gap: 14, marginTop: 20 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <Field label="Fecha del simulacro *">
                  <input style={inp} type="date" value={drillForm.drill_date}
                    onChange={e => setDrillForm(f => ({ ...f, drill_date: e.target.value }))} />
                </Field>
                <Field label="Tipo de simulacro *">
                  <select style={sel} value={drillForm.drill_type}
                    onChange={e => setDrillForm(f => ({ ...f, drill_type: e.target.value as DrillType }))}>
                    {(Object.keys(DRILL_TYPE_LABELS) as DrillType[]).map(t =>
                      <option key={t} value={t}>{DRILL_TYPE_LABELS[t]}</option>
                    )}
                  </select>
                </Field>
              </div>
              <Field label="Lugar">
                <input style={inp} value={drillForm.location}
                  onChange={e => setDrillForm(f => ({ ...f, location: e.target.value }))}
                  placeholder="Ej: Bodega principal" />
              </Field>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <Field label="Duración (minutos)">
                  <input style={inp} type="number" min={0} value={drillForm.duration_minutes}
                    onChange={e => setDrillForm(f => ({ ...f, duration_minutes: e.target.value }))}
                    placeholder="Ej: 20" />
                </Field>
                <Field label="Número de participantes">
                  <input style={inp} type="number" min={0} value={drillForm.participants_count}
                    onChange={e => setDrillForm(f => ({ ...f, participants_count: e.target.value }))}
                    placeholder="Ej: 15" />
                </Field>
              </div>
              <Field label="Incidencias">
                <textarea style={{ ...ta, minHeight: 60 }} value={drillForm.incidents_notes}
                  onChange={e => setDrillForm(f => ({ ...f, incidents_notes: e.target.value }))}
                  placeholder="Incidencias ocurridas durante el simulacro…" />
              </Field>
              <Field label="Lecciones aprendidas">
                <textarea style={{ ...ta, minHeight: 60 }} value={drillForm.lessons_learned}
                  onChange={e => setDrillForm(f => ({ ...f, lessons_learned: e.target.value }))}
                  placeholder="Puntos a mejorar para el próximo simulacro…" />
              </Field>
              {drillFormError && <p style={{ color: '#ef4444', fontSize: '0.82rem', margin: 0 }}>{drillFormError}</p>}
            </div>
            <div style={modalFooter}>
              <button onClick={() => !drillSaving && setShowDrillModal(false)} style={cancelBtn}>Cancelar</button>
              <button onClick={handleSaveDrill} disabled={drillSaving} style={{ ...saveBtn, opacity: drillSaving ? 0.7 : 1 }}>
                {drillSaving ? 'Guardando…' : 'Registrar simulacro'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <label style={{ color: '#94a3b8', fontSize: '0.78rem', fontWeight: 600 }}>{label}</label>
      {children}
    </div>
  );
}

function PlanField({ label, value }: { label: string; value: string | null }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ color: '#64748b', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.03em', textTransform: 'uppercase' }}>{label}</span>
      <span style={{ color: '#cbd5e1', fontSize: '0.86rem', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{value || '—'}</span>
    </div>
  );
}

function statusBadgeStyle(status: PlanStatus): React.CSSProperties {
  const palette = status === 'vigente'
    ? { bg: 'rgba(47,191,113,0.12)', color: '#2FBF71', border: 'rgba(47,191,113,0.3)' }
    : { bg: 'rgba(234,179,8,0.12)',  color: '#eab308', border: 'rgba(234,179,8,0.3)' };
  return {
    background: palette.bg, color: palette.color, border: `1px solid ${palette.border}`,
    borderRadius: 6, padding: '4px 12px', fontSize: '0.78rem', fontWeight: 700,
  };
}

// ── Styles ─────────────────────────────────────────────────────────────────────

const primaryBtn:  React.CSSProperties = { padding: '9px 20px', background: 'linear-gradient(135deg, #1D6FA3 0%, #2FBF71 100%)', border: 'none', color: '#fff', fontWeight: 700, fontSize: '0.88rem', borderRadius: 8, cursor: 'pointer' };
const thStyle:     React.CSSProperties = { padding: '11px 14px', textAlign: 'left', color: '#64748b', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', whiteSpace: 'nowrap' };
const td:          React.CSSProperties = { padding: '11px 14px', verticalAlign: 'middle' };
const ghostBtn:    React.CSSProperties = { padding: '5px 12px', background: 'transparent', border: '1px solid #334155', color: '#94a3b8', borderRadius: 6, cursor: 'pointer', fontSize: '0.76rem', transition: 'all 0.15s' };
const overlay:     React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(3px)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 400, padding: '20px 16px', overflowY: 'auto' };
const modalBox:    React.CSSProperties = { background: '#1e293b', border: '1px solid #334155', borderRadius: 16, width: '100%', maxWidth: 560, boxShadow: '0 24px 48px rgba(0,0,0,0.5)', margin: 'auto' };
const modalHeader: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid #334155' };
const modalFooter: React.CSSProperties = { padding: '16px 24px', borderTop: '1px solid #334155', display: 'flex', gap: 10, justifyContent: 'flex-end', alignItems: 'center' };
const inp:         React.CSSProperties = { width: '100%', padding: '9px 12px', background: 'rgba(255,255,255,0.04)', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0', fontSize: '0.86rem', outline: 'none', boxSizing: 'border-box' };
const sel:         React.CSSProperties = { ...inp, cursor: 'pointer' };
const ta:          React.CSSProperties = { ...inp, resize: 'vertical' };
const closeBtn:    React.CSSProperties = { background: 'none', border: 'none', color: '#64748b', fontSize: '1.2rem', cursor: 'pointer', lineHeight: 1 };
const cancelBtn:   React.CSSProperties = { padding: '9px 20px', background: 'transparent', border: '1px solid #334155', color: '#94a3b8', borderRadius: 8, cursor: 'pointer', fontSize: '0.88rem', fontWeight: 600 };
const saveBtn:     React.CSSProperties = { padding: '9px 24px', background: 'linear-gradient(135deg, #1D6FA3 0%, #2FBF71 100%)', border: 'none', color: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: '0.88rem', fontWeight: 700 };
const warningBannerStyle: React.CSSProperties = { background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 8, padding: '10px 14px', color: '#fbbf24', fontSize: '0.84rem', fontWeight: 600 };
const tabBtnStyle: React.CSSProperties = { padding: '10px 16px', background: 'transparent', border: 'none', borderBottom: '2px solid transparent', color: '#64748b', fontSize: '0.86rem', fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s' };
const tabBtnActiveStyle: React.CSSProperties = { color: '#2FBF71', borderBottomColor: '#2FBF71' };
